import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import * as Sentry from '@sentry/react';
import {
  CompanyShareableType,
  CompliantType,
  getV1CompaniesCompanyIdEquipmentId,
  MatchKeyMatchableType,
  ModelError_Item,
  ModelError_Response,
  postV1CompaniesCompanyIdSites,
  Site_Create,
  SiteType,
} from '@treadinc/horizon-api-spec';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { complianceStatusColumn } from '~components/CompliancePanel/complianceStatusColumn';
import { CSVImportExportButton } from '~components/CSVImportExport/CSVImportExportButton';
import { CSVImportExportModal } from '~components/CSVImportExport/CSVImportExportModal';
import { useCSVModal } from '~components/CSVImportExport/useCSVModal';
import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import LabelList from '~components/Labels/LabelList';
import { matchKeysColumn } from '~components/MatchKeys/matchKeysColumn';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { AddressType } from '~components/Order/SiteSelection/types';
import StatusTabs, { StatusTab } from '~components/Tabs/StatusTabs';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { FeatureFlags } from '~constants/featureFlags';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import { StagedComplianceDoc } from '~hooks/useComplianceDocuments/checklist';
import { useCreateStagedComplianceDocuments } from '~hooks/useComplianceDocuments/useCreateStagedComplianceDocuments';
import useEntityCsvConfig from '~hooks/useCSVImportExport/useEntityCsvConfig';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { Equipment } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { useUserRoles } from '~hooks/useUsers/useUserRoles';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useCompanyMatchKeysQuery from '~queries/useCompanyMatchKeysQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import EquipmentFilters from '../Equipment/EquipmentFilters';
import { EquipmentDialog, EquipmentDialogHandlers } from './components/EquipmentDialog';
import { EquipmentFormValues } from './EquipmentForm';
import { ExternalEquipmentDTO } from './ExternalEquipmentForm';
import { IDMatching } from './IDMatching';

interface ReceiverCompany {
  id: string;
  legalName: string;
}

interface Dispatchable {
  id: string;
  name: string;
}

export interface CompanyShareFormData {
  id: string;
  receiverCompany: ReceiverCompany;
  dispatchable: Dispatchable;
  ticketMatchId?: string;
  companyShareableId: string;
  rateOverride: boolean;
}
enum EquipmentManagementTab {
  EQUIPMENT = 'equipment',
  ID_MATCHING = 'id_matching',
}

enum FleetTab {
  INTERNAL = 'internal',
  VENDOR = 'vendor',
}

const fleetTabs: StatusTab<FleetTab>[] = [
  {
    label: t('administration.equipment.internal_trucks'),
    value: FleetTab.INTERNAL,
  },
  {
    label: t('administration.equipment.vendor_trucks'),
    value: FleetTab.VENDOR,
  },
];

// A truck shared in by an owner-operator has no internal company; the record holder is
// the fleet it belongs to.
const resolveOwnerName = (row: Equipment) =>
  row.internalCompany?.legalName ?? row.companyName;

const EquipmentManagement = observer(() => {
  const hideDocumentTypesEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.hideDocumentTypes,
  });
  const matchKeysEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.matchKeysEnabled,
  });

  const [selectedTab, setSelectedTab] = useState(EquipmentManagementTab.EQUIPMENT);
  const [fleetTab, setFleetTab] = useState<FleetTab>(FleetTab.INTERNAL);
  const fleetInternal = fleetTab === FleetTab.INTERNAL;
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [equipmentIdToDelete, setEquipmentIdToDelete] = useState<string>();
  const [companyShares, setCompanyShares] = useState<CompanyShare[]>([]);
  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
  const [stagedComplianceDocs, setStagedComplianceDocs] = useState<StagedComplianceDoc[]>(
    [],
  );
  const [formCompanyId, setFormCompanyId] = useState<string | undefined>();
  const { createStagedDocs } = useCreateStagedComplianceDocuments(formCompanyId);
  const { companyAssetsStore, userStore, toasterStore } = useStores();
  const { getUserRoles } = useUserRoles();
  const {
    getEquipmentByCompanyId,
    updateEquipment,
    createEquipmentByCompanyId,
    deleteEquipment,
  } = useEquipment();
  const [isUpdating, setIsUpdating] = useState(false);
  const { searchValue, setSearchQueryValue } = useDataGridSearch();
  const {
    getCompanySharesByCompanyId,
    updateCompanyShare,
    createCompanyShareByCompanyId,
    deleteCompanyShare,
    isUpdating: isUpdatingCompanyShares,
  } = useCompanyShares();
  const companyId = getEffectiveUserCompanyId(userStore);
  const showMatchKeys = matchKeysEnabled && userStore.getPermissions().canViewMatchKey;
  const { matchKeysByMatchableId } = useCompanyMatchKeysQuery({
    companyId,
    isEnabled: showMatchKeys,
    matchableType: MatchKeyMatchableType.EQUIPMENT,
  });

  const { config: equipmentConfig, loadConfig: loadEquipmentConfig } = useEntityCsvConfig(
    'equipment',
    companyId || '',
  );

  const {
    goToPage: goToEquipmentPage,
    isFetching: isFetchingEquipment,
    pagination: equipmentPagination,
    reloadCurrentPage: reloadEquipmentPage,
    rows,
    startOver: startEquipmentOver,
  } = useCursorPagination<Equipment>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      getEquipmentByCompanyId({
        companyId,
        cursor,
        filters: companyAssetsStore.equipmentFilters,
        internal: fleetInternal,
        limit,
        link,
        query: searchValue,
        signal,
      }),
    limit: MAX_API_PAGE_SIZE,
  });

  const csvModal = useCSVModal({ onImportComplete: () => void goToEquipmentPage() });

  useEffect(() => {
    getUserRoles();
  }, [getUserRoles]);

  const handleSelectedTabChange = useCallback(
    (__: React.SyntheticEvent, value: EquipmentManagementTab) => {
      setSelectedTab(value);
    },
    [],
  );

  useEffect(() => {
    if (companyId) {
      void startEquipmentOver();
    }
  }, [
    companyId,
    searchValue,
    fleetInternal,
    companyAssetsStore.equipmentFilters,
    startEquipmentOver,
  ]);

  useEffect(() => {
    if (selectedId) {
      getCompanySharesByCompanyId({
        companyId,
        company_shareable_type: CompanyShareableType.EQUIPMENT,
        company_shareable_id: selectedId,
        sender_company_id: companyId,
        callBack: setCompanyShares,
      });
    }
  }, [companyId, selectedId]);

  const getDialogTitle = () => {
    // Both parties to a hand-off see their own truck, so only a further re-share is external.
    if (isExternalEquipment && !isProvisionedEquipment) {
      return t('administration.equipment.update_external_truck');
    }

    return selectedId
      ? t('administration.equipment.update_truck')
      : t('administration.equipment.create_truck');
  };

  const onChangeQuickFilter = (searchQuery: string) => {
    setSearchQueryValue(searchQuery);
  };
  const updateDialogRef = useRef<EquipmentDialogHandlers>(null);

  const handleClose = () => {
    setCompanyShares([]);
    setSelectedId(null);
    setAsyncErrors([]);
    setStagedComplianceDocs([]);
    setDialogOpen(false);
  };
  const editRow = (id: string) => {
    setSelectedId(id);
    setDialogOpen(true);
  };

  const createNew = () => {
    setSelectedId(null);
    setAsyncErrors([]);
    setDialogOpen(true);
  };

  const deleteRow = (id: string) => {
    setEquipmentIdToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const deleteEquipmentCallback = () => {
    if (equipmentIdToDelete) {
      setIsUpdating(true);
      deleteEquipment({ equipmentId: equipmentIdToDelete })
        .then(() => {
          setIsDeleteDialogOpen(false);
          toasterStore.push(
            alert(t('administration.equipment.equipment_deleted'), AlertTypes.success),
          );
          setEquipmentIdToDelete(undefined);
          void reloadEquipmentPage();
        })
        .catch(() => {
          // Error is already handled in the hook
        })
        .finally(() => {
          setIsUpdating(false);
        });
    }
  };

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setEquipmentIdToDelete(undefined);
  };

  const handleErrors = (error: AxiosError<ModelError_Response>) => {
    setAsyncErrors(error.response?.data.error.errors || []);
  };

  const onSuccess = async (eq?: Equipment) => {
    const id = eq?.id;
    const isEditingEquipment = Boolean(selectedId);

    // a new record has no known page, so it returns to page 1; an edit keeps the current page
    if (isEditingEquipment) {
      void reloadEquipmentPage();
    } else {
      void goToEquipmentPage();
    }

    // Do not show a success alert if there is an error alert (handled in connections.ts)
    if (!toasterStore.alerts.length && id) {
      try {
        const response = await getV1CompaniesCompanyIdEquipmentId({
          path: { 'company-id': eq?.company?.id || companyId, id: id },
        });
        const equipment = Equipment.parse(response.data.data);
        const messageArgs = { name: equipment.name, equipment_id: equipment.equipmentId };

        toasterStore.push(
          alert(
            isEditingEquipment
              ? t('administration.equipment.equipment_updated', messageArgs)
              : t('administration.equipment.equipment_created', messageArgs),
            AlertTypes.success,
          ),
        );
      } catch (error) {
        // The save landed; a missing confirmation should not strand the dialog or the list.
        Sentry.captureException(error);
      }

      handleClose();
    }
  };

  const handleDeleteCompanyShares = (
    originalCompanyShares: CompanyShare[],
    companyShares: CompanyShareFormData[],
  ) => {
    const originalCompanySharesExcludingCurrentCompany = originalCompanyShares.filter(
      (companyShare) => companyShare.receiverCompany.id !== companyId,
    );

    const deletedCompanyShares = originalCompanySharesExcludingCurrentCompany.filter(
      (originalCompanyShare) =>
        !companyShares.some(
          (companyShare: { id: string | undefined }) =>
            companyShare.id === originalCompanyShare.id,
        ),
    );

    if (deletedCompanyShares?.length) {
      deletedCompanyShares.forEach((companyShare) => {
        deleteCompanyShare(companyShare.id as string);
      });
    }
  };

  const handleUpdateCompanyShare = async (data: {
    externalId?: string;
    ticketMatchId?: string;
  }) => {
    try {
      await updateCompanyShare({
        companyShareId: selectedEquipment?.companyShare?.id as string,
        external_id: data.externalId || '',
        ticket_match_id: data.ticketMatchId || '',
      });
    } catch (error) {
      handleErrors(error as AxiosError<ModelError_Response>);
      // We need to throw the error to prevent the onSuccess function from being called in the onSubmitCallback
      throw error;
    }
  };

  const handleUpdateEquipment = async (
    data: Equipment & { companySpecificId?: string },
    equipmentCompanyId: string,
  ) => {
    try {
      // Update company specific id if equipment was previously shared
      if (selectedEquipment?.companyShare?.id && data.companySpecificId) {
        await updateCompanyShare({
          companyShareId: selectedEquipment.companyShare.id,
          external_id: data.companySpecificId,
        });
      } else {
        await updateEquipment({ equipment: data, companyId: equipmentCompanyId });
      }
    } catch (error) {
      handleErrors(error as AxiosError<ModelError_Response>);
      // We need to throw the error to prevent the onSuccess function from being called in the onSubmitCallback
      throw error;
    }
  };

  const onSubmitCallback = async (data: EquipmentFormValues | ExternalEquipmentDTO) => {
    const equipmentCompanyId = ('company' in data && data.company?.id) || companyId;
    const originalCompanyShares = companyShares;
    const submittedCompanyShares = (data.companyShares ?? []) as CompanyShareFormData[];

    // If homeBase is a Google suggestion (no siteId), create Site first
    let equipmentData = { ...data };
    if (
      'homeBase' in data &&
      data.homeBase &&
      !data.homeBase.siteId &&
      data.homeBase.type === AddressType.Suggestions &&
      data.homeBase.address
    ) {
      try {
        // Construct site payload directly for postV1CompaniesCompanyIdSites
        // Include all fields that the working Site creation modal includes
        const sitePayload: Site_Create = {
          name: data.homeBase.name ?? '',
          lat: data.homeBase.lat ?? '',
          lon: data.homeBase.lng ?? '',
          site_type: SiteType.EQUIPMENT_HOME_BASE,
          notes: '',
          department_id: null,
          external_id: null,
          // Deparse the AddressItem to API format (guaranteed to exist at this point)
          address: AddressItem.deparseCreate(data.homeBase.address as AddressItem)!,
        };

        const response = await postV1CompaniesCompanyIdSites({
          path: { 'company-id': equipmentCompanyId },
          body: sitePayload,
        });
        const newSiteId = response.data.data.id;

        // Update equipmentData with the new site ID
        equipmentData = {
          ...data,
          homeBase: {
            ...data.homeBase,
            id: newSiteId,
            siteId: newSiteId,
          },
        };
      } catch (error) {
        handleErrors(error as AxiosError<ModelError_Response>);
        return;
      }
    }

    setIsUpdating(true);

    if (selectedId) {
      handleDeleteCompanyShares(originalCompanyShares, submittedCompanyShares);

      const promises = [];

      if (submittedCompanyShares.length) {
        submittedCompanyShares.forEach((companyShare) => {
          if (companyShare.id) {
            // Update existing company share
            promises.push(
              updateCompanyShare({
                companyShareId: companyShare.id,
                dispatchable: companyShare.dispatchable.id === 'dispatchable',
                ticket_match_id: companyShare.ticketMatchId,
              }),
            );
          } else {
            // Create new company share
            promises.push(
              createCompanyShareByCompanyId({
                companyId: equipmentCompanyId,
                company_shareable_id: companyShare.companyShareableId ?? '',
                company_shareable_type: 'Equipment' as CompanyShareableType,
                receiver_company_id: companyShare.receiverCompany.id,
                dispatchable: companyShare.dispatchable.id === 'dispatchable',
                ticket_match_id: companyShare.ticketMatchId,
              }),
            );
          }
        });
      }

      if (isExternalEquipment) {
        promises.push(
          handleUpdateCompanyShare({
            externalId: equipmentData.externalId,
            ticketMatchId:
              'ticketMatchId' in equipmentData ? equipmentData.ticketMatchId : undefined,
          }),
        );
      } else {
        promises.push(
          handleUpdateEquipment(
            equipmentData as unknown as Equipment,
            equipmentCompanyId,
          ),
        );
      }

      Promise.all(promises)
        .then(() => {
          void onSuccess(equipmentData as unknown as Equipment);
        })
        .catch((error) => {
          handleErrors(error as AxiosError<ModelError_Response>);
        })
        .finally(() => {
          setIsUpdating(false);
        });
    } else {
      createEquipmentByCompanyId({
        equipment: equipmentData as EquipmentFormValues,
        companyId: equipmentCompanyId,
      })
        .then(async (res) => {
          if (submittedCompanyShares.length) {
            submittedCompanyShares.forEach((companyShare) =>
              createCompanyShareByCompanyId({
                companyId: equipmentCompanyId,
                company_shareable_id: res.id,
                company_shareable_type: 'Equipment' as CompanyShareableType,
                receiver_company_id: companyShare.receiverCompany.id,
                dispatchable: companyShare.dispatchable.id === 'dispatchable',
                ticket_match_id: companyShare.ticketMatchId,
              }),
            );
          }

          // Create staged compliance documents for the new equipment
          const { failed } = await createStagedDocs(
            CompliantType.EQUIPMENT,
            res.id,
            stagedComplianceDocs,
          );
          if (failed > 0) {
            toasterStore.push(
              alert(
                t('error_messages.compliance_document_types.partial_save_failure'),
                AlertTypes.warn,
              ),
            );
          }

          void onSuccess(res);
        })
        .catch(handleErrors)
        .finally(() => {
          setIsUpdating(false);
        });
    }
  };

  const onSubmitForm = () => {
    updateDialogRef.current?.submit(onSubmitCallback);
  };

  const isGridNarrowed =
    Boolean(searchValue) ||
    Object.values(companyAssetsStore.equipmentFilters).some((value) => !isEmpty(value));
  const emptyFleetText = fleetInternal
    ? t('administration.equipment.no_internal_trucks')
    : t('administration.equipment.no_vendor_trucks');

  const selectedEquipment = useMemo(() => {
    return rows.find((eq) => eq.id === selectedId) || null;
  }, [selectedId, rows]);
  const isExternalEquipment = !!selectedEquipment?.companyShare?.id;
  // Equipment provisioned to one company can still be re-shared onward to others (e.g.
  // Planet Express shares Tread's truck to a third hauler). Only the owner and the
  // provisioned-to company are party to the provisioning itself; a further recipient
  // should see this as an ordinary external share, not as provisioned equipment.
  const isProvisionedEquipment =
    !!selectedEquipment?.internalCompany?.id &&
    selectedEquipment.internalCompany.id !== selectedEquipment?.company?.id &&
    (companyId === selectedEquipment?.company?.id ||
      companyId === selectedEquipment.internalCompany.id);

  const columns = useMemo<Array<DataTableColumn<Equipment>>>(
    () => [
      //Order goes (1) Name, (2) Equipment Type, (3) License Number, (4) Equipment ID
      {
        field: 'name',
        headerName: t('administration.equipment.number_name'),
        renderCell: (equipment) => (
          <Typography variant="body2">
            {equipment.companyShare?.externalId || equipment.name}
          </Typography>
        ),
      },
      {
        field: 'typeName',
        headerName: t('form_fields.equipment_type'),
        renderCell: (equipment) => (
          <Tooltip title={`${t('form_fields.equipment_type')}: ${equipment.typeName}`}>
            <span>{equipment.typeName}</span>
          </Tooltip>
        ),
      },
      ...(fleetInternal
        ? [
            {
              field: 'homeBase',
              headerName: t('administration.equipment.home_base'),
              valueGetter: (equipment: Equipment) => equipment.homeBase?.name ?? '',
            },
          ]
        : [
            {
              field: 'ownerName',
              headerName: t('administration.equipment.owner'),
              valueGetter: resolveOwnerName,
            },
            {
              field: 'sharedBy',
              headerName: t('administration.equipment.shared_by'),
              valueGetter: (equipment: Equipment) => {
                const senderCompany = equipment.companyShare?.senderCompany;
                return senderCompany && senderCompany.id !== equipment.company.id
                  ? senderCompany.legalName
                  : '';
              },
            },
          ]),
      {
        field: 'licenseNumber',
        headerName: t('form_fields.license_number'),
        valueGetter: (equipment) => equipment.licenseNumber,
      },
      {
        field: 'externalId',
        headerName: t('form_fields.equipment_id'),
        valueGetter: (equipment) =>
          equipment.companyShare?.id ? '' : equipment.externalId,
      },
      {
        field: 'ticketMatchId',
        headerName: t('form_fields.ticket_match_number'),
        defaultHidden: true,
        valueGetter: (equipment) => equipment.companyShare?.ticketMatchId || '',
      },
      ...(!hideDocumentTypesEnabled ? [complianceStatusColumn<Equipment>()] : []),
      ...(showMatchKeys ? [matchKeysColumn<Equipment>(matchKeysByMatchableId)] : []),
      {
        field: 'labels',
        headerName: t('form_fields.labels'),
        renderCell: (equipment) => <LabelList labels={equipment.labels} />,
      },
      actionsColumn((equipment) => {
        const options = [
          {
            title: t('actions.delete'),
            icon: <Delete />,
            callBack: () => deleteRow(equipment.id),
          },
        ];

        return (
          <>
            <IconButton size="small" onClick={() => editRow(equipment.id)}>
              <Edit />
            </IconButton>
            <SimpleMenu options={options}>
              <MoreHoriz />
            </SimpleMenu>
          </>
        );
      }),
    ],
    [hideDocumentTypesEnabled, fleetInternal, matchKeysByMatchableId, showMatchKeys],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('administration.equipment.title')}
        description={t('settings.page_descriptions.equipment')}
      />
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: 'column',
        }}
      >
        <Tabs value={selectedTab} onChange={handleSelectedTabChange}>
          <Tab
            label={t('administration.equipment.title')}
            value={EquipmentManagementTab.EQUIPMENT}
          />
          <Tab
            label={t('administration.equipment.id_matching')}
            value={EquipmentManagementTab.ID_MATCHING}
          />
        </Tabs>
      </Box>
      {selectedTab === EquipmentManagementTab.EQUIPMENT && (
        <Box
          data-test-id={'equipment-data-grid'}
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
            mt: 2,
          }}
        >
          <StatusTabs onChange={setFleetTab} selectedTab={fleetTab} tabs={fleetTabs} />
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <EquipmentFilters />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <CSVImportExportButton onClick={csvModal.open} />
              <SmallButton color="brandV2Yellow" onClick={createNew} startIcon={<Add />}>
                {t('administration.equipment.create_equipment')}
              </SmallButton>
            </Box>
          </Box>
          <DataTable
            id="equipment-data-grid"
            columns={columns}
            rows={rows}
            loading={isFetchingEquipment || isUpdating}
            noRowsOverlayText={isGridNarrowed ? undefined : emptyFleetText}
            onSearchChange={onChangeQuickFilter}
            headerTrailingComponent={
              <DataGridPager
                isFetching={isFetchingEquipment || isUpdating}
                onNavigate={(link) => void goToEquipmentPage(link)}
                pagination={equipmentPagination}
              />
            }
          />
        </Box>
      )}
      {selectedTab === EquipmentManagementTab.ID_MATCHING && <IDMatching />}
      <EquipmentDialog
        ref={updateDialogRef}
        isOpen={isDialogOpen}
        onClose={handleClose}
        selectedEquipment={selectedEquipment}
        companyShares={companyShares}
        companyId={companyId}
        isExternalEquipment={isExternalEquipment}
        isUpdating={isUpdating}
        isUpdatingCompanyShares={isUpdatingCompanyShares}
        asyncErrors={asyncErrors}
        onSubmit={onSubmitForm}
        dialogTitle={getDialogTitle()}
        onFormCompanyChange={setFormCompanyId}
        onStagedComplianceDocsChange={setStagedComplianceDocs}
      />
      <ConfirmationDialog
        isBusy={isUpdating}
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={deleteEquipmentCallback}
        title={t('administration.equipment.delete_equipment_title')}
      >
        <Typography>{t('administration.equipment.delete_equipment_text')}</Typography>
      </ConfirmationDialog>
      <CSVImportExportModal
        isOpen={csvModal.isOpen}
        onClose={csvModal.close}
        config={equipmentConfig}
        loadConfig={loadEquipmentConfig}
        companyId={companyId || ''}
        entityDisplayName={t('administration.equipment.title')}
        onImportComplete={csvModal.handleImportComplete}
      />
    </>
  );
});

export { EquipmentManagement };
